// disable eslint check for new-cap because every openlayer object is lowercase
/* eslint-disable new-cap */

import Draw from 'ol/interaction/Draw'
import olModify from 'ol/interaction/Modify'
import olCollection from 'ol/Collection'
import { never } from 'ol/events/condition'
import store from '@/vuex/store'
import olWKT from 'ol/format/WKT'
import olFeature from 'ol/Feature'
import olPolygon from 'ol/geom/Polygon'
import olLineString from 'ol/geom/LineString'
import olMultiPoint from 'ol/geom/MultiPoint'
import olLayerVector from 'ol/layer/Vector'
import olSourceVector from 'ol/source/Vector'
import olGeoJSON from 'ol/format/GeoJSON'
import { getArea } from 'ol/sphere'

import olStyleStroke from 'ol/style/Stroke'
import olStyleFill from 'ol/style/Fill'
import olStyleText from 'ol/style/Text'
import olStyleStyle from 'ol/style/Style'
import olStyleIcon from 'ol/style/Icon'
import olStyleCircle from 'ol/style/Circle'

import pencilSvg from './pencil.svg'
import proj4 from 'proj4'
import convert from 'color-convert'
import projectionUtil from '@/utils/projectionUtil'
import contourVertexUtil from '@/utils/contourVertexUtil'
import renderHandlers from '@/components/openLayers/renderHandlers'
import i18n from '@/configurations/app/config-locales'

// The map decides per background layer whether its labels read white on black or the other way round.
// The draw handlers have no map or component to ask, so openLayers.vue hands the setting over.
let invertMapTextColour = false

// A polygon smaller than this on screen has no room for a second line under its name.
const MINIMUM_LABEL_WIDTH_IN_PIXELS = 90
const MINIMUM_LABEL_HEIGHT_IN_PIXELS = 40

// How close to a vertex the pointer has to be to grab it or to remove it with a right-click.
const VERTEX_PIXEL_TOLERANCE = 10

export default {
  convertGeometryWKT(geometryWKT) {
    const formatWKT = new olWKT()
    const getGeoJSON = new olGeoJSON()
    const featureGeometry = formatWKT.readGeometry(geometryWKT)
    const geoJSONFormat = getGeoJSON.writeGeometryObject(featureGeometry)

    return this.setGeometryData(geoJSONFormat)
  },

  setGeometryData(geoJSONFormat) {
    const geometryLonLatData = []
    if (geoJSONFormat.type === 'Point') {
      return geometryLonLatData
    }

    if (geoJSONFormat.type === 'LineString') {
      geoJSONFormat.coordinates.forEach((coordinates) => {
        geometryLonLatData.push(this.geometryConversion(coordinates))
      })
      return geometryLonLatData
    }

    // One entry per polygon with all its rings, so callers must not wrap it like a single ring
    if (geoJSONFormat.type === 'MultiPolygon') {
      return geoJSONFormat.coordinates.map(polygon =>
        polygon.map(ring => ring.map(coordinates => this.geometryConversion(coordinates))))
    }

    geoJSONFormat.coordinates[0].forEach((coordinates) => {
      geometryLonLatData.push(this.geometryConversion(coordinates))
    })
    return geometryLonLatData
  },

  geometryConversion(coordinates) {
    const conversionResult = proj4('EPSG:4326', 'EPSG:3857', [coordinates[0], coordinates[1]])
    const dataToSave = [conversionResult[0], conversionResult[1]]
    return dataToSave
  },

  async refreshSubLocations() {
    const SlDataEntry = await store.dispatch('getSubLocations', { projectId: store.state.route.params.projectId })
    const subLocationsWithPolygonData = []
    if (SlDataEntry) {
      const filledPolygons = SlDataEntry.filter(location => location.SlPolygonGeometry && location.SlPolygonGeometry !== 'POLYGON EMPTY')

      filledPolygons.forEach(item => {
        subLocationsWithPolygonData.push({
          type: 'subLocation',
          name: item.SlName,
          location: item.SlGuid,
          featureCount: Number(item.SlID),
          ...this.contourCoordinates(item.SlPolygonGeometry)
        })
      })
    }

    return subLocationsWithPolygonData
  },

  async refreshProjectContour() {
    const prDataEntry = store.getters.getCurrentProject
    if (prDataEntry && prDataEntry.PrPolygonGeometry && prDataEntry.PrPolygonGeometry !== 'POLYGON EMPTY') {
      return {
        type: 'project',
        name: prDataEntry.PrName,
        location: prDataEntry.PrGuid,
        ...this.contourCoordinates(prDataEntry.PrPolygonGeometry),
        featureCount: 0
      }
    }
    return null
  },

  /**
   * The geometry type and map coordinates of a stored contour. A MULTIPOLYGON (e.g. a grondverzet
   * zone of separate parcels) keeps one entry per polygon, which loadDrawFeatures splits into parts.
   * @param {string} geometryWKT contour as WKT in EPSG:4326
   * @returns {{ geometryType: string, coords: Array }}
   */
  contourCoordinates(geometryWKT) {
    const geometryLonLatData = this.convertGeometryWKT(geometryWKT)
    const wkt = geometryWKT.trim()
    if (/^LINESTRING/i.test(wkt)) {
      return { geometryType: 'LineString', coords: geometryLonLatData }
    }
    if (/^MULTIPOLYGON/i.test(wkt)) {
      return { geometryType: 'MultiPolygon', coords: geometryLonLatData }
    }
    return { geometryType: 'Polygon', coords: [geometryLonLatData] }
  },

  async refreshDrawFeatures() {
    const drawFeatures = await this.refreshSubLocations()
    const projectContour = await this.refreshProjectContour()

    if (projectContour) {
      drawFeatures.push(projectContour)
    }

    if (drawFeatures.length > 0) {
      localStorage.setItem('drawFeatures', JSON.stringify(drawFeatures))
    }

    return drawFeatures
  },

  /**
   * Checks if the user is using the application in online- or offline-modus
   * @returns Boolean
   */
  get hasInternetConnection() {
    return window.navigator.onLine
  },
  /**
   * Create the empty draw layer and its source.
   * @param {*} ctx Open Layers map component
  */
  createDrawLayer (ctx) {
    // The top layer watcher only fires on a change, so take the setting the map starts with.
    this.setMapTextColourInverted(ctx.invertMapTextColour)

    ctx.drawSource = new olSourceVector({
      format: new olGeoJSON(),
      projection: 'EPSG:4326'
    })

    ctx.drawLayer = new olLayerVector(
      Object.assign({
        category: 'ProjectData',
        name: 'Contour',
        opacity: 1,
        opacityPercentage: 100,
        editable: true,
        preload: Infinity,
        // Layers can join the map after it has been built, so the stacking order of the contours
        // can no longer come from their place in the layer array.
        zIndex: 50,
        source: ctx.drawSource,
        isDraw: true
      }, ctx.mapLayersConfig['Contour']))
  },

  /**
   * Fill the draw layer with the contours of the project and its sublocations. Needs the network,
   * so it runs beside the map rather than in front of it.
   * @param {*} ctx Open Layers map component
  */
  async loadDrawFeatures (ctx) {
    const storedDrawFeatures = localStorage.getItem('drawFeatures')
    const drawFeatures = this.hasInternetConnection ? await this.refreshDrawFeatures() : JSON.parse(storedDrawFeatures) || []

    drawFeatures.forEach(({ coords, type, location, name, featureCount, geometryType }, index) => {
      // If the coordinates are deep nested, it means it's a MultiPolygon
      if (Array.isArray(coords[0][0][0])) {
        coords.forEach((singleCoords, subIndex) => {
          const featureObject = this.createDrawFeature({ type, location, name, geometry: new olPolygon(singleCoords), featureCount }, `${index}-${subIndex}`)
          // A contour is saved as a single POLYGON, so saving an edited part would drop the other parts.
          featureObject.set('isMultiPolygonPart', true)
          ctx.drawSource.addFeature(featureObject)
        })
      } else {
        // plain Polygon or LineString
        const geometry = geometryType === 'LineString' ? new olLineString(coords) : new olPolygon(coords)
        const featureObject = this.createDrawFeature({ type, location, name, geometry, featureCount }, index)
        ctx.drawSource.addFeature(featureObject)
      }
    })
  },

  /**
   * Add draw interaction. Handles leaving draw mode and storing shape layer data in local storage.
   * @param {Object} ctx Open Layers map component
   */
  addDrawInteraction (ctx) {
    ctx.drawFeature = new Draw({
      source: ctx.drawSource,
      type: 'Polygon',
      style: new olStyleStyle({
        image: new olStyleIcon({
          src: pencilSvg,
          scale: 1.5,
          anchor: [0, 0],
          anchorOrigin: 'bottom-left'
        }),
        stroke: new olStyleStroke({
          color: 'rgba(59, 157, 206, 0.8)',
          width: 2
        }),
        fill: new olStyleFill({
          color: 'rgba(255,255,255,0.2)'
        })
      })
    })

    const drawHandlerCtx = this
    ctx.drawFeature.on('drawend', function (evt) {
      const feature = evt.feature
      const { type, location, name } = ctx.drawMode

      feature.values_.type = type
      feature.values_.location = location
      feature.values_.name = name

      // Time margin to store contour in layer before saving it to local storage
      setTimeout(() => {
        ctx.toggleClickToDrawContour(false, null)
        drawHandlerCtx.addContourToDraw(feature, ctx.drawSource)
      }, 200)
    })
  },

  /**
   * Create a contour feature from a pasted WKT string and run it through the regular contour save flow.
   * @param {*} ctx Open Layers map component
   * @param {*} param1 pasted contour data: { type, location, wkt, epsg }. The WKT coordinates are in the given epsg projection.
   */
  addPastedContourToDraw (ctx, { type, location, wkt, epsg }) {
    projectionUtil.loadProj4Defs()

    const formatWKT = new olWKT()
    // A pasted LINESTRING stays a line, a POLYGON stays a polygon
    const geometry = formatWKT.readGeometry(wkt, { dataProjection: epsg, featureProjection: 'EPSG:3857' })
    const feature = new olFeature({ type, location, geometry })

    ctx.drawSource.addFeature(feature)
    this.addContourToDraw(feature, ctx.drawSource)

    ctx.map.getView().fit(geometry.getExtent(), {
      padding: [50, 50, 50, 50],
      maxZoom: 18,
      duration: 500
    })
  },

  /**
   * Create new feature, with id and applied style
   * @param {*} param0 feature values: { type, location, name, geometry }
   * @param {*} id unique feature id
   * @returns vector feature with applied style
   */
  createDrawFeature ({ type, location, name, geometry: polygon, featureCount }, id) {
    const featureObject = new olFeature({ type, location, name, geometry: polygon, featureCount })
    featureObject.setId(`poly-${id}`)
    this.applyDrawStyle(featureObject, type, name, featureCount)

    return featureObject
  },

  surfaceAreaInSquareMeters(geometry) {
    if (!geometry) return 0

    return Math.round(getArea(geometry, { projection: 'EPSG:3857' }) * 100) / 100
  },

  formatSurfaceArea(surfaceArea) {
    // A drawn contour is never accurate to the square centimetre and the decimals only make the label
    // longer. Below a hundred square metres one of them still says something.
    const decimals = surfaceArea < 100 ? 1 : 0
    const formattedArea = surfaceArea.toLocaleString(i18n.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })

    return `${formattedArea} m²`
  },

  /**
   * Whether the polygon is big enough on screen to carry its surface area under its name. The name
   * itself is what identifies the contour and is always drawn; the area is the second line.
   */
  fitsSurfaceAreaLabel(geometry, resolution) {
    if (!geometry || !resolution) return true

    const [minX, minY, maxX, maxY] = geometry.getExtent()

    return (maxX - minX) / resolution >= MINIMUM_LABEL_WIDTH_IN_PIXELS &&
      (maxY - minY) / resolution >= MINIMUM_LABEL_HEIGHT_IN_PIXELS
  },

  applyDrawStyle (featureObject, type, name, featureCount) {
    const hslColor = this.getFeatureHSLColor(featureCount)
    const rgbColor = convert.hsl.rgb(hslColor[0], hslColor[1], hslColor[2])

    // A style function, so the label can follow the background layer and the zoom. The colour is
    // worked out here and not inside it: past twenty features getFeatureHSLColor picks a random hue,
    // which would make the contour change colour on every render.
    featureObject.setStyle((feature, resolution) => this.buildDrawStyles(feature, resolution, rgbColor, type, name))
  },

  buildDrawStyles(feature, resolution, rgbColor, type, name) {
    const colours = renderHandlers.mapTextColours(invertMapTextColour)
    const textFill = new olStyleFill({ color: colours.fill })
    const textStroke = new olStyleStroke({ color: colours.stroke, width: colours.strokeWidth })

    const surfaceArea = type === 'subLocation' ? this.surfaceAreaInSquareMeters(feature.getGeometry()) : 0
    const showsSurfaceArea = surfaceArea > 0 && this.fitsSurfaceAreaLabel(feature.getGeometry(), resolution)

    const styles = [
      new olStyleStyle({
        fill: new olStyleFill({
          color: `rgba(${rgbColor.join(',')}, 0.20)`,
          weight: 1
        }),
        stroke: new olStyleStroke({
          color: `rgba(${rgbColor.join(',')}, 0.8)`,
          width: 2.2
        }),
        text: new olStyleText({
          textAlign: 'center',
          textBaseline: 'middle',
          font: 'bold 16px sans-serif',
          text: name,
          fill: textFill,
          stroke: textStroke,
          // Lifted so the pair of lines sits centred on the contour, rather than the name alone.
          offsetY: showsSurfaceArea ? -9 : 0,
          placement: 'point',
          overflow: true
        })
      })
    ]

    if (showsSurfaceArea) {
      styles.push(new olStyleStyle({
        text: new olStyleText({
          textAlign: 'center',
          textBaseline: 'middle',
          font: '13px sans-serif',
          text: this.formatSurfaceArea(surfaceArea),
          fill: textFill,
          stroke: textStroke,
          offsetY: 10,
          placement: 'point',
          overflow: true
        })
      }))
    }

    if (feature.get('isEditing')) {
      styles.push(this.buildEditVertexStyle(rgbColor))
    }

    return styles
  },

  buildEditVertexStyle(rgbColor) {
    return new olStyleStyle({
      image: new olStyleCircle({
        radius: 5,
        fill: new olStyleFill({ color: 'white' }),
        stroke: new olStyleStroke({ color: `rgb(${rgbColor.join(',')})`, width: 2 })
      }),
      geometry: (feature) => new olMultiPoint(this.uniqueVertices(feature.getGeometry()))
    })
  },

  uniqueVertices(geometry) {
    if (geometry.getType() === 'LineString') {
      return geometry.getCoordinates()
    }

    return geometry.getCoordinates()[0].slice(0, -1)
  },

  setMapTextColourInverted(inverted) {
    invertMapTextColour = inverted === true
  },

  getFeatureHSLColor(featureCount) {
    if (featureCount >= 20) {
      const randomHue = Math.random() * 360
      return [randomHue, 100, 40]
    }

    const standardHueColors = [255, 190, 70, 0, 213, 128, 43]
    const hueColorIndex = featureCount % standardHueColors.length
    const hueColor = standardHueColors[hueColorIndex]

    let saturationDecreasePercentage = Math.floor(featureCount / standardHueColors.length) * 20
    let saturationPercentage = 80 - saturationDecreasePercentage

    let lightnessPercentage = 50
    if (featureCount >= 14) {
      lightnessPercentage = 40
    } else if (featureCount >= 7) {
      lightnessPercentage = 70
    }

    return [hueColor, saturationPercentage, lightnessPercentage]
  },

  addContourToDraw: function(feature, drawSource) {
    const type = feature.values_.type

    switch (type) {
      case 'subLocation':
        this.addSubLocationContourToDraw(feature, drawSource)
        break
      case 'project':
        this.addProjectContourToDraw(feature, drawSource)
        break
      default:
        console.log('Invalid contour type: ' + type)
    }
  },

  featureGeometryAsWKT(feature) {
    const geometry = feature.getGeometry()
    const isLineString = geometry.getType() === 'LineString'

    const geometryLonLatCoordinates = []
    const geometryCoordinates = isLineString ? geometry.getCoordinates() : geometry.getCoordinates()[0]
    geometryCoordinates.forEach((v) => {
      const conversionResult = proj4('EPSG:3857', 'EPSG:4326', [v[0], v[1]])
      const dataToSave = [conversionResult[0], conversionResult[1]]
      geometryLonLatCoordinates.push(dataToSave)
    })

    const geometryLonLatCoordinate = geometryLonLatCoordinates.join('_').split(',').join(' ').trim().replaceAll('_', ',')
    const reformattedWellKnownToken = isLineString ? `LINESTRING (${geometryLonLatCoordinate})` : `POLYGON ((${geometryLonLatCoordinate}))`
    return reformattedWellKnownToken
  },

  /**
   * Adds style to newly created sublocation contour and updates local storage
   * @param {*} feature newly created feature
   * @param {*} drawSource vector features layer source
   */
  addSubLocationContourToDraw: function(feature, drawSource) {
    const { subLocations } = store.state
    const { type, location } = feature.values_
    const subLocation = subLocations.find(sl => sl.SlGuid === location)
    if (!subLocation) return

    // there's only one contour per sublocation, so if the user draws multiple, remove the previous ones
    this.removeMatchingFeatures(drawSource, oldFeature =>
      oldFeature !== feature &&
      oldFeature.values_.type === 'subLocation' &&
      oldFeature.values_.name === subLocation.SlName)

    feature.values_.name = subLocation.SlName
    this.applyDrawStyle(feature, type, subLocation.SlName, Number(subLocation.SlID))

    this.saveSubLocationContour(feature, subLocation, drawSource)
  },

  saveSubLocationContour(feature, subLocation, drawSource) {
    const PolygonData = {
      PrID: store.state.route.params.projectId,
      SlID: subLocation.SlID,
      SlGuid: subLocation.SlGuid,
      SlPolygonGeometry: this.featureGeometryAsWKT(feature),
      SlSurfaceGroundLevel: this.surfaceAreaInSquareMeters(feature.getGeometry())
    }

    if (this.hasInternetConnection === true) {
      store.dispatch('setContourOfItem', PolygonData)
    }

    this.updateDrawFeaturesInLocalStorage(drawSource)
  },

  updateDrawFeaturesInLocalStorage: function(drawSource) {
    const features = drawSource.getFeatures().map(({ values_ }) => ({
      coords: values_.geometry.getCoordinates(),
      type: values_.type,
      location: values_.location,
      name: values_.name,
      geometryType: values_.geometry.getType()
    }))

    localStorage.setItem('drawFeatures', JSON.stringify(features))
  },

  /**
   * Adds style to newly created project feature and updates local storage
   * @param {*} feature newly created feature
   * @param {*} drawSource vector features layer source
   */
  addProjectContourToDraw: function(feature, drawSource) {
    if (!store.state.projects || !store.state.projects[0]) {
      return
    }

    const { type } = feature.values_
    const project = store.state.projects[0]

    // there's only one project contour, so if the user draws multiple, remove the old ones
    this.removeMatchingFeatures(drawSource,
      oldFeature => oldFeature !== feature && oldFeature.values_.type === 'project')

    feature.values_.name = project.PrName
    // project should always have the feature count of 0
    this.applyDrawStyle(feature, type, project.PrName, 0)

    this.saveProjectContour(feature, project)
  },

  saveProjectContour(feature, project) {
    project.PrPolygonGeometry = this.featureGeometryAsWKT(feature)

    store.dispatch('setProject', { projectData: project })
  },

  saveEditedContour(feature, drawSource) {
    const { type, location } = feature.values_

    if (type === 'subLocation') {
      const subLocation = store.state.subLocations.find(sl => sl.SlGuid === location)
      if (subLocation) this.saveSubLocationContour(feature, subLocation, drawSource)
      return
    }

    if (type === 'project' && store.state.projects && store.state.projects[0]) {
      this.saveProjectContour(feature, store.state.projects[0])
    }
  },

  startEditingContour(ctx, feature) {
    this.stopEditingContour(ctx)

    ctx.editingDrawFeature = feature
    ctx.editingContourName = feature.values_.name
    feature.set('isEditing', true)
    // Setting a property does not re-render the feature, and the vertices have to appear.
    feature.changed()

    ctx.modifyInteraction = new olModify({
      features: new olCollection([feature]),
      pixelTolerance: VERTEX_PIXEL_TOLERANCE,
      // Removing a vertex is done with a right-click instead, see removeEditedContourVertex.
      deleteCondition: never
    })
    ctx.modifyInteraction.on('modifyend', () => {
      // A notice that a point could not be removed no longer applies once the shape has changed.
      ctx.contourEditNotice = null
      this.saveEditedContour(feature, ctx.drawSource)
    })
    ctx.map.addInteraction(ctx.modifyInteraction)
  },

  stopEditingContour(ctx) {
    if (!ctx.editingDrawFeature) return

    ctx.map.removeInteraction(ctx.modifyInteraction)
    ctx.modifyInteraction = null

    ctx.editingDrawFeature.unset('isEditing')
    ctx.editingDrawFeature.changed()
    ctx.editingDrawFeature = null
    ctx.editingContourName = null
  },

  removeEditedContourVertex(ctx, pixel) {
    const feature = ctx.editingDrawFeature
    if (!feature) return 'noVertex'

    const geometry = feature.getGeometry()
    const vertexPixels = this.uniqueVertices(geometry).map(vertex => ctx.map.getPixelFromCoordinate(vertex))
    const vertexIndex = contourVertexUtil.nearestVertexIndex(vertexPixels, pixel, VERTEX_PIXEL_TOLERANCE)
    if (vertexIndex === -1) return 'noVertex'

    const coordinates = contourVertexUtil.coordinatesWithoutVertex(geometry.getType(), geometry.getCoordinates(), vertexIndex)
    if (!coordinates) return 'tooFewVertices'

    geometry.setCoordinates(coordinates)
    this.saveEditedContour(feature, ctx.drawSource)
    return 'removed'
  },

  removeMatchingFeatures: function(drawSource, predicate) {
    drawSource.getFeatures().forEach(feature => {
      if (predicate(feature)) {
        drawSource.removeFeature(feature)
      }
    })
  },

  /**
   * Delete feature from draw layer and update localstorage
   * @param {*} context Open Layers map component
   */
  deleteDrawFeature (context) {
    const type = context.selectedDrawFeature.values_.type

    switch (type) {
      case 'subLocation':
        this.deleteSubLocationDrawFeature(context)
        break
      case 'project':
        this.deleteProjectDrawFeature(context)
        break
      default:
        console.log('Invalid contour type: ' + type)
    }
  },

  deleteSubLocationDrawFeature(context) {
    const { subLocations } = store.state
    const subLocation = subLocations.find(location => location.SlGuid === context.selectedDrawFeature.values_.location)

    if (subLocation) {
      context.drawSource.removeFeature(context.selectedDrawFeature)
      context.selectedDrawFeature = null

      const SublocationPolygonData = {
        PrID: store.state.route.params.projectId,
        SlID: subLocation.SlID,
        SlGuid: subLocation.SlGuid,
        SlPolygonGeometry: null,
        SlSurfaceGroundLevel: null
      }

      store.dispatch('deleteContourOfItem', SublocationPolygonData)

      this.updateDrawFeaturesInLocalStorage(context.drawSource)
    }
  },

  deleteProjectDrawFeature(context) {
    if (!store.state.projects || !store.state.projects[0]) {
      return
    }

    const project = store.state.projects[0]
    project.PrPolygonGeometry = ''
    store.dispatch('setProject', { projectData: project })

    context.drawSource.removeFeature(context.selectedDrawFeature)
    context.selectedDrawFeature = null

    this.updateDrawFeaturesInLocalStorage(context.drawSource)
  }
}
